
import React from "react";
import { Investor } from "@/types/investor";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Mail, Sparkles, Eye, CheckCircle } from "lucide-react";
import { getRelevanceBadgeVariant } from "@/utils/uiHelpers";
import { hasInvestorBeenEmailed } from "@/utils/emailUtils";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { hookDebugUtils } from "@/utils/ai/debugUtils";

interface InvestorListItemProps {
  investor: Investor;
  isSelected: boolean;
  isEnriching: boolean;
  onSelectInvestor: (investor: Investor) => void;
  onEnrichInvestor?: (investor: Investor) => Promise<Investor>;
  isInvestorEnriching?: (id: string) => boolean;
  isInvestorProcessed: (investor: Investor) => boolean;
  onViewInvestor: (investor: Investor) => void;
  onShowSources?: (investor: Investor) => void;
}

const InvestorListItem = ({
  investor,
  isSelected,
  onSelectInvestor,
  onEnrichInvestor,
  isInvestorEnriching,
  isInvestorProcessed,
  onViewInvestor,
  onShowSources
}: InvestorListItemProps) => {
  const isEmailed = hasInvestorBeenEmailed(investor.id);
  const isCurrentlyEnriching = isInvestorEnriching ? isInvestorEnriching(investor.id) : false;
  const hasSources = investor.sources && investor.sources.length > 0;
  const processed = isInvestorProcessed(investor);

  // Helper function to get confidence badge variant
  const getConfidenceBadgeVariant = (confidence?: string) => {
    switch (confidence) {
      case 'high': return "success";
      case 'medium': return "warning";
      case 'low': return "destructive";
      default: return "secondary";
    }
  };

  // Safe render helper to prevent rendering objects directly
  const safeRender = (value: any): string => {
    if (value === null || value === undefined) return "";
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'boolean') return value.toString();
    return hookDebugUtils.safeObjectDisplay(value) || "";
  };

  return (
    <tr className={isSelected ? "bg-gray-100" : undefined} data-state={isSelected ? "selected" : undefined}>
      <td className="p-4 border-b">
        <div className="flex items-center gap-2">
          {safeRender(investor.firm)}
          {isEmailed && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <CheckCircle size={16} className="text-green-600" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Email has been sent</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {investor.confidence && processed && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Badge 
                    variant={getConfidenceBadgeVariant(investor.confidence)}
                    className="ml-1 text-xs"
                  >
                    {safeRender(investor.confidence)}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-sm">
                    {investor.confidence === 'high' 
                      ? 'Information verified from multiple reliable sources' 
                      : investor.confidence === 'medium'
                      ? 'Information found but not fully verified'
                      : 'Limited verifiable information found'}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {hasSources && onShowSources && (
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-6 w-6 p-0"
              onClick={(e) => {
                e.stopPropagation();
                onShowSources(investor);
              }}
            >
              <span className="text-xs text-blue-600 underline">{investor.sources?.length}</span>
            </Button>
          )}
        </div>
      </td>
      <td className="p-4 border-b">
        {processed ? (
          <Badge variant={getRelevanceBadgeVariant(investor.relevanceScore)}>
            {typeof investor.relevanceScore === 'number' ? investor.relevanceScore.toFixed(1) : '0.0'}
          </Badge>
        ) : (
          <Badge variant="outline" className="bg-gray-100 text-gray-500">
            Unprocessed
          </Badge>
        )}
      </td>
      <td className="p-4 border-b max-w-[300px] truncate" title={investor.investmentFocus}>
        {safeRender(investor.investmentFocus) || "Not available"}
      </td>
      <td className="p-4 border-b">
        {investor.lastRelevanceUpdate ? (
          <span className="text-xs text-gray-500">
            Updated: {new Date(investor.lastRelevanceUpdate).toLocaleDateString()}
          </span>
        ) : (
          <span className="text-xs text-gray-500">Not processed</span>
        )}
      </td>
      <td className="p-4 border-b">
        <div className="flex gap-2">
          <Button 
            variant="secondary" 
            onClick={() => onSelectInvestor(investor)} 
            size="sm"
          >
            <Mail className="mr-1 h-3 w-3" /> Select
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onViewInvestor(investor);
            }}
          >
            <Eye className="mr-1 h-3 w-3" /> View
          </Button>
          
          {onEnrichInvestor && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onEnrichInvestor(investor);
              }}
              disabled={isCurrentlyEnriching}
            >
              <Sparkles className="mr-1 h-3 w-3" />
              {isCurrentlyEnriching ? "Enriching..." : (processed ? "Re-enrich" : "Enrich")}
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
};

export default InvestorListItem;
